import { Component, createRef, h, RefObject } from 'preact';
import produce, { Draft } from 'immer';
import { IntlProvider, translate } from 'preact-i18n';
import { debounce } from 'helpers/debounce';
import {
  getKustomer,
  getKustomerCore,
  getKustomerLocalStorage,
  runKustomerListenersForEvent,
  setKustomerLocalStorage,
} from 'widget_main/globals/helpers';
import Widget from 'widget_main/components/Widget';

import {
  ChatAvailabilityContext,
  ConnectionContext,
  ConversationsContext,
  HideNewConversationButtonSettingsContext,
  KBContext,
  MessagesContext,
  NavigationContext,
  ProactiveChatContext,
  SettingsContext,
  UnreadCountsContext,
  WidgetActionsContext,
} from 'widget_main/store';
import {
  ConversationEntity,
  ConversationsState,
  Page,
  ProactiveChatCallbackResponse,
} from 'widget_main/store/types';
import {
  CONVERSATIONS_PATH,
  MAX_SESSION_HISTORY_PREFETCH_ITERATIONS,
  KB_ARTICLE_PATH,
  KB_SEARCH_PATH,
  kustomerWidgetEventTypes,
  KUSTOMER_CUSTOM_HISTORY_FILTER_EVENT,
  KUSTOMER_HISTORY_STATUS_FILTER_EVENT,
  MESSAGE_THREAD_PATH,
  OFFLINE,
  WIDGET_TYPE_CHAT,
  WIDGET_TYPE_CHAT_KB,
  WIDGET_TYPE_KB,
} from 'widget_main/globals/constants';
import ChatRootIcon from 'widget_main/components/ChatRootIcon';
import { ChatSessionsData } from 'core_main/api/sessions/types';
import { SatisfactionCallbackResponse } from 'core_main/api/satisfaction/types';
import {
  MessageAttachmentCallbackResponse,
  MessageCallbackResponse,
} from 'core_main/sdk/send_message/types';
import { InitAssistantCallbackResponse } from 'core_main/sdk/init_assistant/types';
import { getMappedLanguage } from 'i18n/helpers';
import { handleError } from 'globals/errors';
import { Polling } from 'widget_main/app/polling';

import {
  CreateConversationEventDetail,
  CreateConversationWidgetApiParameters,
  CreateConversationInDraftEventDetail,
  CreateConversationInDraftWidgetApiParameters,
  OpenConversationByIdEventDetail,
} from 'widget/widget_api/types';
import {
  ConversationDeletedCallbackResponse,
  ConversationMergedCallbackResponse,
  UrlPreviewCallbackResponse,
} from 'core_main/pubnub/types';
import { isMobileViewport } from 'widget_main/globals/dom';
import {
  appendChatNotificationToParentTitle,
  clearChatNotificationFromParentTitle,
  focusOnIframe,
  focusOnWindowContainingIframe,
  isWidgetInFocus,
  setIframeSizeChatIcon,
  setIframeSizeHidden,
} from 'helpers/kustomer_api_iframe';
import { resizeWidgetHelper } from 'helpers/kustomer_api_iframe/helpers';

import { GetConversationsCallbackResponse } from 'core_main/sdk/get_conversations/types';
import { createCategoryTree } from 'widget_main/screens/KBSearchList/helpers';
import { ArticleCallbackResponse } from 'core_main/sdk/get_articles/types';
import { getStore, updateStore } from 'globals/coreStore';
import * as chatHistoryStatusFilter from 'globals/chatHistoryStatusFilter';
import * as helpers from './helpers';
import * as assistantHelpers from './helpers/assistant';
import { AppProps, AppState } from './types';
import { INITIAL_STATE } from './constants';
import defaultMessages from './messages';
import {
  fetchMessageHistoryHelper,
  mergeAndSortByMessageTimestamps,
} from './helpers/message';
import { publicConstants } from '../../widget_api/constants';
import { lastOpenedPageIsSupportedByWidgetType } from './helpers';

const kustomerCore = getKustomerCore();

const DEFAULT_PROACTIVE_CHAT_INTERVAL = 1000;
const PROACTIVE_CHAT_RETRY_MULTIPLIER = 250;
const MAX_PROACTIVE_CHAT_FAILURES = 16;
const HISTORY_PAGE_REQUEST_TIMEOUT_MS = 30000;
const HISTORY_PAGE_REQUEST_TIMEOUT_ERROR_NAME =
  'HistoryPageRequestTimeoutError';

const createHistoryPageRequestTimeoutError = (
  page: number,
  pageSize: number,
) => {
  const error = new Error(
    `Timed out fetching conversations response for page ${page} with page size ${pageSize}`,
  );
  error.name = HISTORY_PAGE_REQUEST_TIMEOUT_ERROR_NAME;
  return error;
};

const isHistoryPageRequestTimeoutError = (e: unknown) =>
  e instanceof Error && e.name === HISTORY_PAGE_REQUEST_TIMEOUT_ERROR_NAME;

class App extends Component<AppProps, AppState> {
  proactiveChatInterval;

  proactiveChatTries = 0;

  chatRootIconRef: RefObject<HTMLDivElement>;

  pollIntervalTimer: NodeJS.Timer | undefined;

  constructor() {
    super();
    this.state = {
      ...INITIAL_STATE,
      navigation: {
        ...INITIAL_STATE.navigation,
        updatePage: this.updatePage,
        updateCurrentConversationId: this.updateCurrentConversationId,
        updateInitAssistantPayload: this.updateInitAssistantPayload,
        updateConversationListScrollLocation: debounce(
          this.updateConversationListScrollLocation,
          50,
        ),
      },
      widgetActions: {
        openWidget: this.openWidget,
        closeWidget: this.closeWidget,
      },
      conversations: {
        ...INITIAL_STATE.conversations,
        addConversation: this.addConversation,
        endConversation: this.endConversation,
        updateConversation: this.updateConversation,
        addOptimisticMessage: this.addOptimisticMessage,
        toggleOptimisticMessageError: this.toggleOptimisticMessageError,
        removeOptimisticMessage: this.removeOptimisticMessage,
        fetchMessageHistory: this.fetchMessageHistory,
        fetchNextPageOfHistory: this.fetchNextPageOfHistory,
        refreshConversations: this.refreshConversations,
        setHistoryStatusFilter: this.setHistoryStatusFilter,
        setCustomHistoryFilter: this.setCustomHistoryFilter,
      },
      messages: {
        ...INITIAL_STATE.messages,
        receiveMessage: this.messageReceivedHandler,
        markArticleVisited: this.markArticleVisited,
        resetHandoff: this.resetHandoff,
        resetDraftHandoff: this.resetDraftHandoff,
      },
      kb: {
        ...INITIAL_STATE.kb,
        updateCategories: this.updateCategories,
        updateArticleSearchResults: this.updateArticleSearchResults,
        updateArticleSearchFetching: this.updateArticleSearchFetching,
        updateFeaturedArticles: this.updateFeaturedArticles,
        updateArticles: this.updateArticles,
        updateCurrentCategoryId: this.updateCurrentCategoryId,
        updateCurrentArticleId: this.updateCurrentArticleId,
        updateArticleSearchInput: this.updateArticleSearchInput,
        articleSearchFetching: false,
      },
      polledMessageCount: 0,
    };

    this.resizeHandler = debounce(this.resizeHandler, 150);
    this.chatRootIconRef = createRef();
  }

  resizeWidget = (page?: Page) => {
    const { navigation, isChatHidden, showWidget } = this.state;
    const { hideChatIcon } = this.props;
    resizeWidgetHelper({
      page: page || navigation.page,
      isChatHidden,
      showWidget,
      hideChatIcon,
    });
  };

  setHistoryStatusFilter = (
    next: chatHistoryStatusFilter.HistoryStatusFilterValue,
  ) => {
    updateStore('historyStatusFilter', next);
    this.refreshCachedHistoryFilters();
  };

  setCustomHistoryFilter = (
    clauses:
      | readonly chatHistoryStatusFilter.CustomHistoryFilterClause[]
      | undefined,
  ) => {
    const normalized =
      chatHistoryStatusFilter.normalizeCustomHistoryFilterClauses(clauses);
    updateStore(
      'customHistoryFilter',
      normalized.length ? normalized : undefined,
    );
    this.refreshCachedHistoryFilters();
  };

  refreshCachedHistoryFilters = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.conversations.data = {
          ...draftState.conversations.data,
        };
      }),
    );
  };

  handleHistoryStatusFilterEvent = (event: Event) => {
    const detail = (event as CustomEvent).detail as {
      filter?: unknown;
    };
    const next = chatHistoryStatusFilter.parseHistoryStatusFilterValue(
      detail?.filter,
    );
    this.setHistoryStatusFilter(next);
  };

  handleCustomHistoryFilterEvent = (event: Event) => {
    const detail = (event as CustomEvent).detail as {
      clauses?:
        | readonly chatHistoryStatusFilter.CustomHistoryFilterClause[]
        | null;
    };
    this.setCustomHistoryFilter(detail?.clauses ?? undefined);
  };

  resizeHandler = () => {
    if (this.state.showWidget) {
      this.resizeWidget();
      this.setState(
        produce((draftState: Draft<AppState>) => {
          draftState.isMobile = isMobileViewport();
        }),
      );
    }
  };

  addKustomerCoreListeners = () => {
    kustomerCore.addListener(
      'onMessageReceived',
      this.agentMessageReceivedHandler,
    );

    kustomerCore.addListener(
      'onUnreadCountChange',
      this.unreadCountChangeHandler,
    );

    kustomerCore.addListener(
      'onConversationEnded',
      this.conversationEndedHandler,
    );

    kustomerCore.addListener(
      'onConversationUnended',
      this.conversationEndedHandler,
    );

    kustomerCore.addListener(
      'onSatisfactionReceived',
      this.satisfactionReceivedHandler,
    );

    kustomerCore.addListener(
      'onConversationMerged',
      this.conversationMergedHandler,
    );

    kustomerCore.addListener('onCustomerDelete', this.customerDeleteHandler);

    kustomerCore.addListener(
      'onConversationDeleted',
      this.conversationDeletedHandler,
    );

    kustomerCore.addListener('onLogout', this.handleLogout);

    kustomerCore.addListener('onAssistantEnded', this.endAssistantHandler);

    kustomerCore.addListener(
      'onSessionTitleChanged',
      this.sessionTitleChangedHandler,
    );
    kustomerCore.addListener(
      'onSessionCustomAttributesUpdated',
      this.sessionCustomAttributesUpdatedHandler,
    );
    kustomerCore.addListener(
      'onUrlPreviewReceived',
      this.urlPreviewReceivedHandler,
    );
  };

  removeKustomerCoreListeners = () => {
    kustomerCore.removeListener(
      'onMessageReceived',
      this.agentMessageReceivedHandler,
    );

    kustomerCore.removeListener(
      'onUnreadCountChange',
      this.unreadCountChangeHandler,
    );

    kustomerCore.removeListener(
      'onConversationEnded',
      this.conversationEndedHandler,
    );

    kustomerCore.removeListener(
      'onConversationUnended',
      this.conversationEndedHandler,
    );

    kustomerCore.removeListener('onLogout', this.handleLogout);

    kustomerCore.removeListener('onAssistantEnded', this.endAssistantHandler);

    kustomerCore.removeListener(
      'onConversationMerged',
      this.conversationMergedHandler,
    );

    kustomerCore.removeListener('onCustomerDelete', this.customerDeleteHandler);

    kustomerCore.removeListener(
      'onConversationDeleted',
      this.conversationDeletedHandler,
    );

    kustomerCore.removeListener(
      'onSessionTitleChanged',
      this.sessionTitleChangedHandler,
    );
    kustomerCore.removeListener(
      'onSessionCustomAttributesUpdated',
      this.sessionCustomAttributesUpdatedHandler,
    );
    kustomerCore.removeListener(
      'onUrlPreviewReceived',
      this.urlPreviewReceivedHandler,
    );
  };

  sessionTitleChangedHandler = (session: ChatSessionsData) => {
    if (!session?.id || !this.state.conversations.data[session.id]) return;
    const previewDetailsNameRaw = session.attributes?.previewDetails?.name;
    const previewDetailsName =
      typeof previewDetailsNameRaw === 'string'
        ? previewDetailsNameRaw.trim() || undefined
        : undefined;
    this.updateConversation(session.id, {
      name: session.attributes?.name,
      previewDetailsName,
    });
  };

  createConversationFromSession = (session: ChatSessionsData) => {
    const custom = chatHistoryStatusFilter.normalizeSessionCustomAttributes(
      session.attributes.custom,
    );
    return {
      preview: session.attributes.preview,
      createdAt: session.attributes.createdAt,
      lastMessageAt: session.attributes.lastMessageAt,
      conversationId: session.id,
      ended: !!session.attributes.lockedAt,
      isInAssistantMode: session.attributes.isInAssistantMode,
      messages: [],
      optimisticMessages: [],
      assistantId: '',
      hasAllMessages: false,
      ...(custom ? { custom } : {}),
    };
  };

  sessionCustomAttributesUpdatedHandler = (session: ChatSessionsData) => {
    if (!session?.id) return;
    const custom = chatHistoryStatusFilter.normalizeSessionCustomAttributes(
      session.attributes.custom,
    );
    const currentConversation = this.state.conversations.data[session.id];
    const currentCustom =
      chatHistoryStatusFilter.normalizeSessionCustomAttributes(
        currentConversation?.custom,
      );
    if (
      currentConversation &&
      chatHistoryStatusFilter.areConversationCustomAttributesEqual(
        currentCustom,
        custom,
      )
    ) {
      return;
    }
    const conversation = currentConversation
      ? { ...currentConversation }
      : this.createConversationFromSession(session);
    if (custom) {
      conversation.custom = custom;
    } else {
      delete conversation.custom;
    }

    this.setState(
      produce((draftState: Draft<AppState>) => {
        if (draftState.conversations.data[session.id]) {
          if (custom) {
            draftState.conversations.data[session.id].custom = custom;
          } else {
            delete draftState.conversations.data[session.id].custom;
          }
        } else {
          draftState.conversations.data[session.id] = conversation;
        }
      }),
      () => {
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onSessionCustomAttributesUpdated,
          { conversation },
        );
      },
    );
  };

  notifySessionOpened = (conversationId?: string) => {
    if (!conversationId) return;
    const conversation = this.state.conversations.data[conversationId];
    if (!conversation) return;

    const { customHistoryFilter } = getStore();
    const custom = chatHistoryStatusFilter.normalizeSessionCustomAttributes(
      conversation.custom,
    );
    const customAttributesOutsideActiveFilter =
      chatHistoryStatusFilter.customAttributesOutsideActiveFilter(
        custom,
        customHistoryFilter,
      );

    runKustomerListenersForEvent(kustomerWidgetEventTypes.onSessionOpened, {
      conversation,
      customAttributesOutsideActiveFilter,
    });
  };

  attachPendingUrlPreviewsForMessageId = (
    draftState: Draft<AppState>,
    messageId: string,
  ) => {
    if (!draftState.messages.pendingUrlPreviews) {
      draftState.messages.pendingUrlPreviews = {};
    }
    const pendingPreviews = draftState.messages.pendingUrlPreviews[messageId];
    if (pendingPreviews?.length) {
      const message = draftState.messages.messageData[messageId];
      if (message) {
        const currentPreviews = message.unfurledPreviews || [];
        const mergedPreviews = [...currentPreviews];
        pendingPreviews.forEach((pendingPreview) => {
          const exists = mergedPreviews.some(
            (p) => p.url === pendingPreview.url,
          );
          if (!exists) {
            mergedPreviews.push(pendingPreview);
          }
        });
        draftState.messages.messageData[messageId] = {
          ...message,
          unfurledPreviews: mergedPreviews,
        };
      }
      delete draftState.messages.pendingUrlPreviews[messageId];
    }
  };

  agentMessageReceivedHandler = (response, error) => {
    const { messages } = this.state;
    const { conversationId, messageId } = response;

    const currentConversationId =
      messages.messageData[messageId]?.conversationId;
    const conversationIdChanged =
      currentConversationId && conversationId !== currentConversationId;

    if (response.sentBy.type === 'user' || conversationIdChanged) {
      this.messageReceivedHandler(response, error);
    }
  };

  handleNotifications = (response) => {
    const { conversationId, body, notificationOverrides } = response;
    const { showWidget } = this.state;
    const { chatSettings, intlDefinitions } = this.props;
    const { teamName, teamIconUrl } = chatSettings;

    // only enable browser notifications if flag is set to true
    const showNotifications =
      getKustomer()?.startParameters?.showBrowserNotifications;

    const sentNotification = (permission: NotificationPermission) => {
      if (permission === 'granted' && (!document.hasFocus() || !showWidget)) {
        const dictionary = intlDefinitions || {
          ...defaultMessages,
        };
        const notificationTitle =
          notificationOverrides.titleOverride ||
          translate(
            'browserNotificationTitle',
            '',
            dictionary,
            {
              teamName,
            },
            0,
            `${teamName} Chat`,
          );
        const notificationBody = notificationOverrides.bodyOverride || body;
        const notification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: teamIconUrl,
        });
        notification.onclick = () => {
          focusOnWindowContainingIframe();
          focusOnIframe();
          this.openConversationById({
            detail: {
              conversationId,
            },
          } as CustomEvent);
          notification.close();
        };
      }
    };

    if (showNotifications && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        sentNotification(Notification.permission);
      } else {
        try {
          Notification.requestPermission().then((permission) =>
            sentNotification(permission),
          );
        } catch (error) {
          // Safari < 15 doesn't return a promise for requestPermissions, when called this way,
          // it will throw a TypeError.
          // If we catch this error, we will try to pass a callback as the first argument instead.
          //  https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission#syntax
          if (error instanceof TypeError) {
            Notification.requestPermission((permission) => {
              sentNotification(permission);
            });
          } else {
            throw error;
          }
        }
      }
    }
  };

  messageReceivedHandler = (response, error) => {
    if (error) {
      handleError(error);
      return;
    }
    const {
      conversationId,
      direction,
      messageId,
      createdAt,
      isInAssistantMode,
    } = response;

    if (direction === 'out') {
      this.handleNotifications(response);
    }
    const { conversations } = this.state;
    if (!conversations.data[conversationId]) {
      kustomerCore.getConversation(
        { conversationId },
        (conversation: ConversationEntity | null, error) => {
          if (!conversation || error) return;
          this.setState(
            produce((draftState: Draft<AppState>) => {
              try {
                conversation.messages = [];
                conversation.optimisticMessages = [];
                draftState.conversations.data[conversationId] = conversation;
                draftState.conversations.data[
                  conversationId
                ].isInAssistantMode = isInAssistantMode;
              } catch (e) {
                handleError(e, {
                  message: 'Error receiving message',
                });
              }
            }),
            () => {
              this.fetchMessageHistory(
                conversationId,
                this.state.conversations.data,
              ).catch((e) =>
                handleError(e, {
                  message:
                    'Message Receive Handler: Error fetching message history',
                }),
              );
            },
          );
        },
      );
      return;
    }
    this.setState((prevState) => {
      const { conversations } = prevState;
      const conversation = conversations.data[conversationId];
      const previousMessagesById = conversation.messages.reduce(
        (acc, messageId) => {
          acc[messageId] = prevState.messages.messageData[messageId];
          return acc;
        },
        {},
      );
      const { messagesById, messages } = mergeAndSortByMessageTimestamps(
        previousMessagesById,
        {
          [messageId]: response,
        },
      );

      return produce(prevState, (draftState: Draft<AppState>) => {
        const lastMessageId = messages[messages.length - 1];
        const lastMessage = messagesById[lastMessageId];
        const conversation = draftState.conversations.data[conversationId];
        conversation.preview = lastMessage?.body;
        conversation.lastMessageAt = createdAt;
        conversation.messages = messages;
        Object.assign(draftState.messages.messageData, messagesById);

        // Check for pending URL previews and attach them to the new message
        // This handles the case where URL preview events arrived before the message
        // (common for outbound messages where preview arrives before message confirmation)
        this.attachPendingUrlPreviewsForMessageId(draftState, messageId);
      });
    });
  };

  unreadCountChangeHandler = (response) => {
    const { total } = response;
    const { conversationId, count } = response.change;
    const { intlDefinitions } = this.props;

    const showTitleNotifications =
      getKustomer()?.startParameters?.showTitleNotifications;

    // we want to update the state before we notify the customer with a title notification
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.unreadCounts.total = total;
        draftState.unreadCounts.conversations[conversationId] = count;
      }),
      () => {
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onUnread,
          response,
        );
      },
    );

    if (showTitleNotifications) {
      const dictionary = intlDefinitions || {
        ...defaultMessages,
      };
      const chatCountString = translate(
        'pageTitleNotification',
        '',
        dictionary,
        {
          number: total,
        },
        total,
      );
      if (!isWidgetInFocus() && total > 0) {
        appendChatNotificationToParentTitle(chatCountString);
      } else {
        clearChatNotificationFromParentTitle(chatCountString);
      }
    }
  };

  conversationEndedHandler = (response) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { conversationId, ended } = response;
        const conversation = draftState.conversations.data[conversationId];
        if (conversation) {
          conversation.ended = ended;
        }
      }),
    );
  };

  endAssistantHandler = (response) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { conversationId, isInAssistantMode } = response;
        const conversation = draftState.conversations.data[conversationId];
        if (conversation) {
          conversation.isInAssistantMode = isInAssistantMode;
        }
      }),
    );
  };

  satisfactionReceivedHandler = (response: SatisfactionCallbackResponse) => {
    const { conversationId } = response;

    if (conversationId)
      this.updateConversation(conversationId, { satisfaction: response });
  };

  /**
   * Handler for URL preview received events from core SDK
   *
   * Processes URL unfurled events and updates message state with preview data.
   * Handles two scenarios:
   * 1. Message exists in state: Attaches preview to existing message
   * 2. Message doesn't exist: Stores preview as pending to attach when message arrives
   *
   * This is critical for outbound messages where the preview event may arrive
   * before the message is stored in state. The pending previews mechanism ensures
   * previews are not lost and are properly attached when messages arrive.
   *
   * CRITICAL: This handler does NOT:
   * - Increment unread counts
   * - Mark messages as delivered
   * - Track read status
   * - Update conversation preview
   *
   * Features:
   * - Prevents duplicate previews by checking URL before adding
   * - Uses Immer for immutable state updates
   * - Emits widget event for customer listeners
   * - Handles edge case where preview arrives before message
   *
   * @param response - Event data containing conversationId, messageId, and urlUnfurled
   * @param response.conversationId - ID of the conversation containing the message
   * @param response.messageId - ID of the message to attach preview to
   * @param response.urlUnfurled - URL unfurled data object
   *
   * @example
   * ```typescript
   * // Called automatically by core SDK event listener
   * this.urlPreviewReceivedHandler({
   *   conversationId: 'conv-123',
   *   messageId: 'msg-456',
   *   urlUnfurled: { unfurledUrl: { url: 'https://example.com', ... } }
   * });
   * ```
   */
  urlPreviewReceivedHandler = (response: UrlPreviewCallbackResponse) => {
    const { conversationId, messageId, urlUnfurled } = response;
    const { messages } = this.state;
    const existingMessage = messages.messageData[messageId];

    // Check for duplicate previews before calling setState
    if (!existingMessage) {
      // Message not in state yet - check if preview is already pending
      const pendingPreviews = messages.pendingUrlPreviews || {};
      const currentPending = pendingPreviews[messageId] || [];
      const newPreviewUrl = urlUnfurled.unfurledUrl.url;
      const alreadyPending = currentPending.some(
        (preview) => preview.url === newPreviewUrl,
      );

      if (alreadyPending) {
        // Preview already pending - emit event and return early (no state change needed)
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onUrlPreviewReceived,
          {
            conversationId,
            message: null,
            urlUnfurled,
          },
        );
        return;
      }
    } else {
      // Check if message already has this preview (duplicate prevention)
      const currentPreviews = existingMessage.unfurledPreviews || [];
      const newPreviewUrl = urlUnfurled.unfurledUrl.url;
      const alreadyExists = currentPreviews.some(
        (preview) => preview.url === newPreviewUrl,
      );

      if (alreadyExists) {
        // Preview already exists - emit event and return early (no state change needed)
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onUrlPreviewReceived,
          {
            conversationId,
            message: existingMessage,
            urlUnfurled,
          },
        );
        return;
      }
    }

    // Update the message in state with the new preview.
    // Duplicate check is inside the updater to avoid race: two events in quick succession
    // can both pass the outer check; the updater sees the latest state and skips if already present.
    const newPreviewUrl = urlUnfurled.unfurledUrl?.url;
    this.setState(
      (prevState) => {
        const { messages: prevMessages } = prevState;
        const prevExistingMessage = prevMessages.messageData[messageId];

        if (!prevExistingMessage) {
          // Message not in state yet - store preview as pending to attach when message arrives
          const pendingPreviews = prevMessages.pendingUrlPreviews || {};
          const currentPending = pendingPreviews[messageId] || [];
          const alreadyPending =
            newPreviewUrl &&
            currentPending.some((p) => p.url === newPreviewUrl);
          if (alreadyPending) return prevState;

          return produce(prevState, (draftState: Draft<AppState>) => {
            if (!draftState.messages.pendingUrlPreviews) {
              draftState.messages.pendingUrlPreviews = {};
            }
            draftState.messages.pendingUrlPreviews[messageId] = [
              ...currentPending,
              urlUnfurled.unfurledUrl,
            ];
          });
        }

        // Avoid duplicate: another setState may have added this URL already
        const currentPreviews = prevExistingMessage.unfurledPreviews || [];
        const alreadyExists =
          newPreviewUrl && currentPreviews.some((p) => p.url === newPreviewUrl);
        if (alreadyExists) return prevState;

        // Update message with new preview
        const updatedMessage: MessageCallbackResponse = {
          ...prevExistingMessage,
          unfurledPreviews: [...currentPreviews, urlUnfurled.unfurledUrl],
        };

        return produce(prevState, (draftState: Draft<AppState>) => {
          draftState.messages.messageData[messageId] = updatedMessage;
        });
      },
      () => {
        const updatedMessage = this.state.messages.messageData[messageId];
        // After state update, emit widget event for customer listeners
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onUrlPreviewReceived,
          {
            conversationId,
            message: updatedMessage || null,
            urlUnfurled,
          },
        );
      },
    );
  };

  conversationMergedHandler = (
    response: ConversationMergedCallbackResponse,
  ) => {
    const { sourceConversationId, targetConversationId } = response;

    // Get the unread count from the source conversation before merging
    const sourceUnreadCount =
      this.state.unreadCounts.conversations[sourceConversationId] || 0;
    const targetUnreadCount =
      this.state.unreadCounts.conversations[targetConversationId] || 0;

    kustomerCore.getConversation(
      {
        conversationId: targetConversationId,
      },
      (conversation) => {
        this.setState(
          produce((draftState: Draft<AppState>) => {
            // Add the target conversation if it doesn't exist
            if (!draftState.conversations.data[targetConversationId]) {
              conversation.messages = [];
              conversation.optimisticMessages = [];
              draftState.conversations.data[targetConversationId] =
                conversation;
            }

            // Mark the source conversation as merged
            if (draftState.conversations.data[sourceConversationId]) {
              draftState.conversations.data[
                sourceConversationId
              ].movedToConversationId = targetConversationId;
            }

            // Consolidate unread counts
            const newTargetUnreadCount = targetUnreadCount + sourceUnreadCount;
            draftState.unreadCounts.conversations[targetConversationId] =
              newTargetUnreadCount;
            // Total remains the same since we're just moving unread counts from source to target

            // Remove the source conversation's unread count
            delete draftState.unreadCounts.conversations[sourceConversationId];
          }),
          () => {
            this.fetchMessageHistory(
              targetConversationId,
              this.state.conversations.data,
            ).catch((e) =>
              handleError(e, {
                message: 'Error fetching history after conversation merge',
              }),
            );
          },
        );
      },
    );
  };

  customerDeleteHandler = () => {
    kustomerCore.logout(() => null);
  };

  conversationDeletedHandler = (
    response: ConversationDeletedCallbackResponse,
  ) => {
    const { conversationId } = response;
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const conversation = draftState.conversations.data[conversationId];
        if (conversation) {
          conversation.deleted = true;
          conversation.ended = true;
        }
        if (draftState.navigation.currentConversationId !== conversationId) {
          delete draftState.conversations.data[conversationId];
        }
      }),
    );
  };

  setInitialNavigation = (settings, startParameters) => {
    const { widgetType } = settings;

    let page;

    switch (widgetType) {
      case WIDGET_TYPE_KB:
        page = KB_SEARCH_PATH;
        break;
      case WIDGET_TYPE_CHAT:
        page = CONVERSATIONS_PATH;
        break;
      case WIDGET_TYPE_CHAT_KB:
        if (
          startParameters.preferredView === publicConstants.CHAT_HISTORY_VIEW
        ) {
          page = CONVERSATIONS_PATH;
        } else {
          page = KB_SEARCH_PATH;
        }
        break;
      default:
        page = CONVERSATIONS_PATH;
    }

    this.updatePage(page);
  };

  handleProactiveChatTrigger = (
    proactiveChat?: ProactiveChatCallbackResponse,
  ) => {
    const isMobile = isMobileViewport();

    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.proactiveChat = proactiveChat;
        draftState.isMobile = isMobile;
      }),
    );
  };

  evaluateProactiveChatRules = () => {
    assistantHelpers
      .getActiveAssistant({
        proactiveOnly: true,
      })
      .then((response) => {
        if (response) {
          const initResponse = {
            ...response,
            initialMessages: response?.initialMessages,
          };
          const callbackResponse: ProactiveChatCallbackResponse = {
            initResponse,
          };
          const { hideHistory } = this.props;
          const { navigation } = this.state;

          // if the customer has an active conversation and the 'hideHistory' setting is enabled,
          //  we need to wait for the Proactive chat rules to be evaluated but do not want to
          //  trigger proactive chat so the end user does not lose their conversation so return early
          if (hideHistory && Boolean(navigation.currentConversationId)) {
            if (this.proactiveChatInterval)
              clearInterval(this.proactiveChatInterval);
            return;
          }

          this.handleProactiveChatTrigger(callbackResponse);
          if (this.proactiveChatInterval)
            clearInterval(this.proactiveChatInterval);
        }

        // if we get a success after a series of failures, restart the interval back to defaults
        if (this.proactiveChatTries > 0) {
          this.proactiveChatTries = 0;
          clearInterval(this.proactiveChatInterval);
          this.proactiveChatInterval = setInterval(
            this.evaluateProactiveChatRules,
            DEFAULT_PROACTIVE_CHAT_INTERVAL,
          );
        }
      })
      .catch((error) => {
        this.proactiveChatTries++;
        let errorMessage = 'Failed to trigger proactive chat:';
        if (this.proactiveChatTries > 1) {
          clearInterval(this.proactiveChatInterval);

          // after 16 tries we will stop trying to ping the assistant endpoint
          if (this.proactiveChatTries <= MAX_PROACTIVE_CHAT_FAILURES) {
            // restart the interval with backoff
            this.proactiveChatInterval = setInterval(
              this.evaluateProactiveChatRules,
              DEFAULT_PROACTIVE_CHAT_INTERVAL +
                PROACTIVE_CHAT_RETRY_MULTIPLIER * this.proactiveChatTries,
            );
          } else {
            errorMessage = 'Proactive chat error maximum reached';
          }
        }
        handleError(error, {
          message: errorMessage,
          tags: { proactiveChat: true },
        });
      });
  };

  startProactiveChatEvaluation = () => {
    if (this.proactiveChatTries === 0) {
      this.evaluateProactiveChatRules();
    }
    this.proactiveChatInterval = setInterval(
      this.evaluateProactiveChatRules,
      DEFAULT_PROACTIVE_CHAT_INTERVAL,
    );
  };

  /**
   * Fetches a single history page from the API (no state merge).
   * Rejects on transport / callback errors so callers can stop prefetch chains.
   */
  requestHistoryPage = (
    page: number,
  ): Promise<GetConversationsCallbackResponse> => {
    const pageSize = helpers.getSessionHistoryPageSize();
    return new Promise<GetConversationsCallbackResponse>((resolve, reject) => {
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        settled = true;
        reject(createHistoryPageRequestTimeoutError(page, pageSize));
      }, HISTORY_PAGE_REQUEST_TIMEOUT_MS);
      const finish = (fn: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        fn();
      };

      const getConversationsParameters = {
        page,
        pageSize,
      };
      kustomerCore.getConversations(
        getConversationsParameters,
        (res, error) => {
          if (error) {
            finish(() => reject(error));
          } else if (!res) {
            finish(() => reject(new Error('No conversations response')));
          } else {
            finish(() => resolve(res));
          }
        },
      );
    });
  };

  applyHistoryPageResponse = (res: GetConversationsCallbackResponse) => {
    const newConversationState = helpers.mapConversationState(
      res.conversations,
      this.state.conversations.data,
    );

    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.unreadCounts = kustomerCore.getUnreadCount();
        Object.assign(draftState.conversations.data, newConversationState);
        draftState.conversations.pages = res.pages;
      }),
    );
  };

  /**
   * Prefetches **every** session history page (page size from {@link helpers.getSessionHistoryPageSize}, default **100**)
   * before the widget finishes boot — parity with **chat-sdk-ios** post-`7.2.1` “load all pages, then show history” behavior.
   * Scroll-based {@link fetchNextPageOfHistory} remains for edge cases where `pages.next` appears later (e.g. new data).
   */
  fetchAllSessionHistoryPages = (): Promise<void> => {
    let nextPage: number | undefined = 1;
    let iterations = 0;

    const step = (): Promise<void> => {
      if (nextPage == null) {
        return Promise.resolve();
      }
      if (iterations >= MAX_SESSION_HISTORY_PREFETCH_ITERATIONS) {
        const err = new Error(
          'Session history prefetch exceeded max iterations',
        );
        handleError(err, {
          message: 'Session history prefetch stopped after max pages:',
          tags: { fetchAllSessionHistoryPages: true },
        });
        return Promise.reject(err);
      }
      iterations += 1;

      return this.requestHistoryPage(nextPage)
        .then((res) => {
          this.applyHistoryPageResponse(res);
          nextPage = res?.pages?.next;
          return step();
        })
        .catch((e) => {
          if (isHistoryPageRequestTimeoutError(e)) {
            return Promise.resolve();
          }
          handleError(e, {
            message: 'Error prefetching session history:',
            tags: { fetchAllSessionHistoryPages: true },
          });
          return Promise.reject(e);
        });
    };

    return step();
  };

  fetchPageOfHistory = (page: number) => {
    return this.requestHistoryPage(page)
      .then((res) => {
        this.applyHistoryPageResponse(res);
        return res;
      })
      .catch((e) => {
        if (isHistoryPageRequestTimeoutError(e)) {
          return undefined;
        }
        // we do not throw here due to a *very fun* issue with mobile browsers that throw an error on _any_ kind of request
        //  cancellation (eg. page close, navigation, bad connection) during a fetch call and the error call is extremely
        //  vague (see also: "TypeError: Load failed" errors)
        //  example: visit this page in an iOS browser https://request-cancellation-test.vercel.app
        //  For now, we will log the error
        handleError(e, {
          message: 'Error fetching history:',
          tags: { fetchPageOfHistory: true },
        });
        return undefined;
      });
  };

  fetchNextPageOfHistory = () => {
    const { next } = this.state.conversations?.pages || {};
    if (!next) return Promise.resolve(undefined);
    return this.fetchPageOfHistory(next);
  };

  setConnectionToOnline = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.connection = 'online';
      }),
    );
  };

  refreshConversations = () => {
    const currentConversations = this.state.conversations.data;
    if (currentConversations && Object.keys(currentConversations).length) {
      const upToPage = this.state.conversations.pages?.current || 1;
      for (let i = 0; i < upToPage; i++) {
        this.fetchPageOfHistory(i);
      }
    }
  };

  handleNetworkOnline = () => {
    this.refreshConversations();
    this.setConnectionToOnline();
  };

  handleNetworkOffline = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.connection = 'offline';
      }),
    );
  };

  handleLogin = (event) => {
    const { parameters, callback } = event.detail;
    const brandId = getKustomer()?.startParameters?.brandId;
    const assistantId = getKustomer()?.startParameters?.assistantId;

    kustomerCore.login(
      { brandId, assistantId, ...parameters },
      (res, error) => {
        if (error) {
          if (typeof callback === 'function') {
            callback(null, error);

            runKustomerListenersForEvent(
              kustomerWidgetEventTypes.onLogin,
              null,
              error,
            );
          }
          return;
        }
        this.resetEndCustomerState(() => {
          this.fetchAllSessionHistoryPages()
            .then(() => {
              if (typeof callback === 'function') {
                callback(res);
              }

              runKustomerListenersForEvent(
                kustomerWidgetEventTypes.onLogin,
                res,
              );
            })
            .catch((historyError) => {
              if (typeof callback === 'function') {
                callback(res, historyError);
              }

              runKustomerListenersForEvent(
                kustomerWidgetEventTypes.onLogin,
                res,
                historyError,
              );
            });
        });
      },
    );
  };

  resetEndCustomerState = (cb) => {
    this.setState((prevState) => {
      return produce(prevState, (draftState: Draft<AppState>) => {
        draftState.conversations = {
          ...prevState.conversations,
          ...INITIAL_STATE.conversations,
        };

        draftState.messages = {
          ...prevState.messages,
          ...INITIAL_STATE.messages,
        };

        draftState.unreadCounts = INITIAL_STATE.unreadCounts;

        draftState.navigation = {
          ...prevState.navigation,
          ...INITIAL_STATE.navigation,
        };
      });
    }, cb);
  };

  handleLogout = (_, error) => {
    if (error) return;
    this.resetEndCustomerState(() => {
      runKustomerListenersForEvent(
        kustomerWidgetEventTypes.onLogout,
        null,
        error,
      );
    });
  };

  checkParentLocationInterval() {
    const { chatSettings } = this.props;
    const { domainCriteria } = chatSettings;

    if (!domainCriteria) return;

    setInterval(() => {
      const parentWindowLocation = window.parent.location.href;
      const { currentLocation, isChatHidden } = this.state;

      if (parentWindowLocation !== currentLocation) {
        const isAllowed = kustomerCore.isUrlAllowed({
          url: parentWindowLocation,
        })?.allowed;

        if (isAllowed) {
          this.setState(
            produce((draftState: Draft<AppState>) => {
              draftState.isChatHidden = false;
              draftState.currentLocation = parentWindowLocation;
            }),
          );
        } else if (isChatHidden === false) {
          this.setState(
            produce((draftState: Draft<AppState>) => {
              draftState.isChatHidden = true;
              draftState.currentLocation = parentWindowLocation;
            }),
          );
        }
      }
    }, 1000);
  }

  restoreWidgetLastOpenState = () => {
    if (assistantHelpers.isAssistantPreview()) return;

    if (getKustomerLocalStorage('wasWidgetOpen') && !isMobileViewport()) {
      const lastOpenedPage = getKustomerLocalStorage('lastOpenPage');
      const { widgetType } = this.props.chatSettings;

      if (
        lastOpenedPageIsSupportedByWidgetType(
          lastOpenedPage as Page,
          widgetType,
        )
      ) {
        if (lastOpenedPage) {
          this.updatePage(lastOpenedPage as Page);
        }

        if (lastOpenedPage === MESSAGE_THREAD_PATH) {
          const lastOpenConversationId = getKustomerLocalStorage(
            'lastOpenConversationId',
          );

          if (
            lastOpenConversationId &&
            this.state.conversations?.data?.[lastOpenConversationId]
          ) {
            this.updateCurrentConversationId(lastOpenConversationId as string);
          } else {
            this.updatePage(CONVERSATIONS_PATH);
          }
        }
      }

      this.openWidget();
    }
  };

  resetHandoff = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { messages } = draftState;
        const { handoff } = messages;

        if (handoff?.assistantId && !handoff?.message) {
          draftState.messages.handoff = {
            assistantId: handoff?.assistantId,
          };
        } else {
          draftState.messages.handoff = undefined;
        }
      }),
    );
  };

  resetDraftHandoff = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.messages.draft = undefined;
      }),
    );
  };

  handoffToMessageThread = (
    assistantId,
    message,
    payload,
    initResponse?,
    initError?,
    callback?: CreateConversationWidgetApiParameters['callback'],
    custom?,
  ) => {
    const handoffCallback = (res, error) => {
      if (typeof callback === 'function') {
        callback(res, error);
      }

      this.state.messages.resetHandoff();
    };

    this.setState(
      produce((draftState: Draft<AppState>) => {
        if (!initError) {
          draftState.messages.handoff = {
            assistantId,
            message,
            payload,
            callback: handoffCallback,
            custom,
          };
          draftState.navigation.currentConversationId = undefined;
          draftState.navigation.page = MESSAGE_THREAD_PATH;
          draftState.navigation.initAssistantPayload = initResponse;
        }
      }),
      () => {
        this.openWidget();
      },
    );
  };

  handoffDraftToMessageThread = (
    message,
    callback?: CreateConversationInDraftWidgetApiParameters['callback'],
  ) => {
    const handoffCallback = (res, error) => {
      if (typeof callback === 'function') {
        callback(res, error);
      }
      this.state.messages.resetDraftHandoff();
    };

    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.messages.draft = {
          message,
          callback: handoffCallback,
        };
        draftState.navigation.currentConversationId = undefined;
        draftState.navigation.page = MESSAGE_THREAD_PATH;
      }),
      () => {
        this.openWidget();
      },
    );
  };

  createConversation = (event) => {
    const detail: CreateConversationEventDetail = event?.detail;
    const message = detail?.message;
    const payload = detail?.payload;
    const { custom } = detail;
    const callback = detail?.callback;

    if (this.state.connection === OFFLINE) {
      if (typeof callback === 'function') {
        return callback(null, new Error('No Internet Connection'));
      }
      return null;
    }
    return assistantHelpers
      .getActiveAssistant({
        assistant: detail?.assistantId,
        dialog: detail.dialogId,
      })
      .then((response) => {
        if (response) {
          this.handoffToMessageThread(
            response.assistant,
            message,
            payload,
            response,
            null,
            callback,
            custom,
          );
        } else {
          this.handoffToMessageThread(
            detail?.assistantId,
            message,
            payload,
            null,
            null,
            callback,
            custom,
          );
        }
      })
      .catch((error) => {
        if (error && typeof callback === 'function') {
          callback(null, error);
        }
      });
  };

  createConversationInDraft = (event) => {
    const detail: CreateConversationInDraftEventDetail = event?.detail;
    const message = detail?.message;
    const callback = detail?.callback;
    if (this.state.connection === OFFLINE) {
      if (typeof callback === 'function') {
        return callback(null, new Error('No Internet Connection'));
      }
      return null;
    }
    return this.handoffDraftToMessageThread(message, callback);
  };

  openKbArticle = (e: { detail: { articleId: string; cb } }) => {
    const { articleId, cb } = e.detail;
    const lang = getMappedLanguage();
    const { knowledgeBaseId } = this.props.chatSettings;
    kustomerCore.getArticleById(
      {
        articleId,
        lang,
        knowledgeBaseId,
      },
      (response, error) => {
        if (error) {
          if (typeof cb === 'function') {
            cb(null, error);
          } else {
            throw error;
          }
          return;
        }
        this.state.kb.updateArticles([response]);
        this.state.kb.updateCurrentArticleId(articleId);
        this.state.navigation.updatePage(KB_ARTICLE_PATH);

        this.openWidget();

        if (typeof cb === 'function') {
          cb(response);
        }
      },
    );
  };

  fetchCategoryArticles(categoryId, categories, cb) {
    const lang = getMappedLanguage();

    const { kb } = this.state;
    const { rootCategory } = kb;
    const { knowledgeBaseId } = this.props.chatSettings;
    kustomerCore.getArticles(
      { categoryId, lang, knowledgeBaseId },
      (res, err) => {
        const updatedArticles = err ? [] : res.articles;

        kb.updateArticles(updatedArticles);

        kb.updateCategories(
          {
            ...categories,
            [categoryId]: {
              ...categories[categoryId],
              articles: updatedArticles,
            },
          },
          rootCategory,
        );
        cb();
      },
    );
  }

  navigateToCategory(categoryId: string, cb?) {
    const { kb, navigation } = this.state;

    kb.updateCurrentCategoryId(categoryId);
    navigation.updatePage(KB_SEARCH_PATH);
    this.openWidget();
    if (typeof cb === 'function') {
      cb(null);
    }
  }

  openKbCategory = (e: { detail: { categoryId: string; cb } }) => {
    const { categoryId, cb } = e.detail;

    const lang = getMappedLanguage();

    const { kb, navigation } = this.state;
    const { categories } = kb;
    const { knowledgeBaseId } = this.props.chatSettings;

    // No categories. This means the KB has never been opened yet, either because the widget wasn't opened or the use
    // was only on the chat screen. Load all categories and then load articles for that category.
    if (!Object.keys(categories).length) {
      kustomerCore.getCategories({ lang, knowledgeBaseId }, (res, err) => {
        if (err) {
          const langNotFound =
            err?.title === 'Not Found' && err?.source === 'lang';

          // Don't callback with error if issue is with lang. Component will gracefully handle that error.
          this.navigateToCategory(categoryId);
          if (!langNotFound && typeof cb === 'function') {
            cb(null, err);
          }
          return;
        }

        const tree = createCategoryTree(res.categories);
        kb.updateCategories(tree, res.rootCategory);

        if (!tree[categoryId]) {
          // Category doesn't exist.
          // Callback with error, but still open kb to root category list so user doesn't have degraded experience.
          this.navigateToCategory(res.rootCategory.categoryId);
          if (typeof cb === 'function') {
            cb(null, new Error('Category not found'));
          }
          return;
        }

        this.fetchCategoryArticles(categoryId, tree, () => {
          this.navigateToCategory(categoryId, cb);
        });
      });
    } else if (!categories[categoryId]) {
      // Category doesn't exist.
      // Callback with error, but still open kb to root category list so user doesn't have degraded experience.
      navigation.updatePage(KB_SEARCH_PATH);
      this.openWidget();
      if (typeof cb === 'function') {
        cb(null, new Error('Category not found'));
      }
    } else if (!categories[categoryId].articles) {
      // Categories have been fetched already, but we need to load the articles.
      this.fetchCategoryArticles(categoryId, categories, () => {
        this.navigateToCategory(categoryId, cb);
      });
    } else {
      // Everything already loaded, just navigate.
      this.navigateToCategory(categoryId, cb);
    }
  };

  handleRestart = () => {
    this.setState({ loading: true, retryCount: this.state.retryCount + 1 });
    kustomerCore.stop().then(() => {
      this.removeKustomerCoreListeners();
      this.resetEndCustomerState(() => {
        this.props.reloadSettings().then(() => {
          this.initApp();
        });
      });
    });
  };

  componentDidMount() {
    window.addEventListener('online', this.handleNetworkOnline);
    window.addEventListener('offline', this.handleNetworkOffline);
    window.addEventListener('kustomerOpen', this.openWidget);
    window.addEventListener('kustomerClose', this.closeWidget);
    window.addEventListener(
      'kustomerCreateConversation',
      this.createConversation,
    );
    window.addEventListener(
      'kustomerOpenDraftConversation',
      this.createConversationInDraft,
    );
    window.addEventListener('kustomerLogin', this.handleLogin);
    window.addEventListener(
      'kustomerOpenConversationById',
      this.openConversationById,
    );
    window.addEventListener('kustomerOpenKbArticle', this.openKbArticle);
    window.addEventListener('kustomerOpenKbCategory', this.openKbCategory);
    window.addEventListener(
      'kustomerGetOpenConversations',
      this.getOpenConversations,
    );
    window.addEventListener(
      KUSTOMER_HISTORY_STATUS_FILTER_EVENT,
      this.handleHistoryStatusFilterEvent,
    );
    window.addEventListener(
      KUSTOMER_CUSTOM_HISTORY_FILTER_EVENT,
      this.handleCustomHistoryFilterEvent,
    );
    window.parent.addEventListener('resize', this.resizeHandler);

    if (kustomerCore) {
      this.initApp();
    }
  }

  /**
   * Generates an array of promises to fetch message history for active conversations.
   *
   * This function iterates over the active conversations and:
   * - Skips conversations that are **deleted** or **ended**.
   * - Returns a promise that fetches the message history for each valid conversation.
   *
   * @returns {Promise<void>[]} An array of promises for fetching message history.
   */
  pollingConversationFetches = () => {
    const { data } = this.state.conversations;
    const conversationFetches = Object.keys(data).map((conversationId) => {
      if (
        data[conversationId]?.deleted === true || // Skip deleted messages
        data[conversationId]?.ended === true // Skip ended conversations
      ) {
        return Promise.resolve();
      }
      return this.fetchMessageHistory(conversationId, data);
    });
    const realtimeRecovery =
      kustomerCore.recoverRealtimeIdentityIfDrifted() ?? Promise.resolve();
    return [realtimeRecovery, ...conversationFetches];
  };

  initApp = () => {
    this.setState({
      isChatHidden:
        ['hidden', 'disabled'].includes(this.props.chatAvailability || '') ||
        !kustomerCore.isUrlAllowed({
          url: window.parent.location.href,
        })?.allowed,
    });

    this.fetchAllSessionHistoryPages()
      .catch((error) => {
        handleError(error, {
          message: 'Error prefetching session history during initialization:',
          tags: { initApp: true },
        });
      })
      .then(() => {
        const kustomer = getKustomer();
        if (!assistantHelpers.isAssistantPreview()) {
          this.startProactiveChatEvaluation();
        }
        kustomer.startFinished = true;

        if (
          !['hidden', 'disabled'].includes(
            this.props.chatAvailability as string,
          )
        ) {
          this.checkParentLocationInterval();
        }

        this.setInitialNavigation(
          this.props.chatSettings,
          kustomer.startParameters,
        );

        window.parent.dispatchEvent(new CustomEvent('kustomerStart'));

        assistantHelpers.startUpdatePageInterval(this.props.chatSettings);

        this.addKustomerCoreListeners();

        this.restoreWidgetLastOpenState();
        const startCallback = kustomer?.startCallback;

        this.setState({ loading: false });
        kustomerCore.kustomerEventCount({ name: 'app.initialization.finish' });
        if (typeof startCallback === 'function') startCallback();

        // poll for messages from pubnub, with our API as a backup on interval
        Polling.restartPolling(this, this.pollingConversationFetches);
      });
  };

  componentDidUpdate(_, prevState) {
    const { isChatHidden } = this.state;
    const { navigation } = this.state;
    if (!prevState.isChatHidden && isChatHidden) {
      setIframeSizeHidden();
    }

    if (prevState.isChatHidden && !isChatHidden) {
      this.resizeWidget();
    }

    const openedConversationId = navigation.currentConversationId;
    if (
      navigation.page === MESSAGE_THREAD_PATH &&
      openedConversationId &&
      (prevState.navigation.page !== MESSAGE_THREAD_PATH ||
        prevState.navigation.currentConversationId !== openedConversationId)
    ) {
      this.notifySessionOpened(openedConversationId);
    }
  }

  componentWillUnmount() {
    this.removeKustomerCoreListeners();
    window.removeEventListener('online', this.handleNetworkOnline);
    window.removeEventListener('offline', this.handleNetworkOffline);
    window.removeEventListener('kustomerOpen', this.openWidget);
    window.removeEventListener('kustomerClose', this.closeWidget);
    window.removeEventListener(
      'kustomerCreateConversation',
      this.createConversation,
    );
    window.removeEventListener(
      'kustomerOpenDraftConversation',
      this.createConversationInDraft,
    );
    window.removeEventListener('kustomerLogin', this.handleLogin);
    window.removeEventListener('kustomerOpenKbArticle', this.openKbArticle);
    window.removeEventListener(
      'kustomerOpenConversationById',
      this.openConversationById,
    );
    window.removeEventListener(
      'kustomerGetOpenConversations',
      this.getOpenConversations,
    );
    window.removeEventListener(
      KUSTOMER_HISTORY_STATUS_FILTER_EVENT,
      this.handleHistoryStatusFilterEvent,
    );
    window.removeEventListener(
      KUSTOMER_CUSTOM_HISTORY_FILTER_EVENT,
      this.handleCustomHistoryFilterEvent,
    );
    window.parent.removeEventListener('resize', this.resizeHandler);
    Polling.clearPolling();
  }

  fetchMessageHistory = (
    conversationId: string,
    conversationDataState: ConversationsState['data'],
  ) => {
    const conversation = conversationDataState[conversationId];
    if (!conversation) return Promise.resolve();
    const currentMessagesById = conversation.messages.reduce(
      (acc, messageId) => {
        acc[messageId] = this.state.messages.messageData[messageId];
        return acc;
      },
      {},
    );
    return fetchMessageHistoryHelper(conversation, currentMessagesById).then(
      (result) => {
        const {
          satisfaction,
          messagesById: newMessagesById,
          hasAllMessages,
          initialMessages,
          optimisticMessages,
        } = result;
        this.setState(
          produce((draftState: Draft<AppState>) => {
            // we must merge conversation messages again here, messagesById could be stale if a message is sent between when it was created and here
            // - ideally we do not merge in two places, however existing fetching logic requires it
            // get messagesById from state
            const conversation = draftState.conversations.data[conversationId];
            const prevMessagesById = conversation.messages.reduce(
              (acc, messageId) => {
                acc[messageId] = draftState.messages.messageData[messageId];
                return acc;
              },
              {},
            );

            const {
              messagesById: mergedMessagesById,
              messages: mergedMessages,
            } = mergeAndSortByMessageTimestamps(
              prevMessagesById,
              newMessagesById,
            );

            const draftConversation =
              draftState.conversations.data[conversationId];
            draftConversation.messages = mergedMessages;
            draftConversation.satisfaction =
              draftConversation.satisfaction || satisfaction;
            draftConversation.hasAllMessages = hasAllMessages;
            if (initialMessages) {
              draftConversation.initialMessages = initialMessages;
            }
            if (optimisticMessages) {
              draftConversation.optimisticMessages = optimisticMessages;
            }
            Object.assign(draftState.messages.messageData, mergedMessagesById);
            Object.keys(mergedMessagesById).forEach((messageId) => {
              this.attachPendingUrlPreviewsForMessageId(draftState, messageId);
            });
            draftState.polledMessageCount =
              (this.state.polledMessageCount || 0) + result.messages.length;
          }),
        );
      },
    );
  };

  openWidget = (event?: CustomEvent | MouseEvent | KeyboardEvent) => {
    const { isChatHidden, showWidget } = this.state;
    if (isChatHidden || showWidget) {
      return;
    }

    const isMobile = isMobileViewport();

    this.setState(
      (prevState) =>
        produce(prevState, (draftState: Draft<AppState>) => {
          draftState.showWidget = true;
          draftState.isMobile = isMobile;
        }),
      () => {
        this.resizeWidget();

        setKustomerLocalStorage('wasWidgetOpen', true);

        if (event?.detail?.callback) {
          event.detail.callback();
        }

        kustomerCore.sendPresenceActivity({ presence: 'online' });

        runKustomerListenersForEvent(kustomerWidgetEventTypes.onOpen);
      },
    );
  };

  closeWidget = (event?: CustomEvent | MouseEvent) => {
    const { isChatHidden } = this.state;
    const { hideChatIcon } = this.props;

    if (isChatHidden) {
      return;
    }

    setIframeSizeChatIcon(hideChatIcon);
    this.setState(
      (prevState) =>
        produce(prevState, (draftState: Draft<AppState>) => {
          draftState.showWidget = false;
        }),
      () => {
        this.chatRootIconRef.current?.focus();

        setKustomerLocalStorage('wasWidgetOpen', false);
        if (event?.detail?.callback) {
          event.detail.callback();
        }

        kustomerCore.sendPresenceActivity({ presence: 'offline' });

        runKustomerListenersForEvent(kustomerWidgetEventTypes.onClose);
        kustomerCore.kustomerEventCount({
          name: 'app.close_widget.click',
        });
      },
    );
  };

  openConversationById = (event: CustomEvent) => {
    const { conversations } = this.state;

    const detail: OpenConversationByIdEventDetail = event?.detail;

    const conversationId = detail?.conversationId;
    const callback = detail?.callback;

    if (!conversationId || !conversations.data[conversationId]) {
      if (callback) {
        return callback(null, new Error('Invalid conversationId provided.'));
      }
    } else {
      return this.setState(
        (prevState) =>
          produce(prevState, (draftState: Draft<AppState>) => {
            draftState.navigation.currentConversationId = conversationId;
            draftState.navigation.page = MESSAGE_THREAD_PATH;
          }),
        () => {
          this.openWidget();
          if (callback) callback(null);
        },
      );
    }

    return undefined;
  };

  getOpenConversations = (event: CustomEvent) => {
    const { conversations } = this.state;

    const openConversations = Object.values(conversations.data)
      .filter((conversation) => !conversation.ended)
      .map((conversation) => ({
        conversationId: conversation.conversationId,
      }));

    if (event?.detail?.callback) {
      event.detail.callback(openConversations);
    }
  };

  updatePage = (page: Page) => {
    this.resizeWidget(page);
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.page = page;
      }),
    );
  };

  updateCurrentConversationId = (conversationId?: string) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.currentConversationId = conversationId;
      }),
    );
  };

  updateInitAssistantPayload = (
    initAssistantPayload: InitAssistantCallbackResponse | undefined,
  ) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.initAssistantPayload = initAssistantPayload;
      }),
    );
  };

  updateConversationListScrollLocation = (location: number) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.conversationListScrollLocation = location;
      }),
    );
  };

  addConversation: ConversationsState['addConversation'] = (
    conversation,
    initialMessages,
  ) => {
    const { conversationId, createdAt, isInAssistantMode, assistantId } =
      conversation;
    const {
      name,
      preview,
      lastResponder,
      previewDetailsName,
      lastMessageAt,
      ended,
      custom,
    } = conversation;
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.conversations.data[conversationId] = {
          conversationId,
          messages: [],
          optimisticMessages: [],
          createdAt: createdAt ?? '',
          isInAssistantMode,
          initialMessages,
          assistantId: assistantId ?? '',
          hasAllMessages: true,
          ...(preview !== undefined ? { preview } : {}),
          ...(lastMessageAt !== undefined ? { lastMessageAt } : {}),
          ...(lastResponder ? { lastResponder } : {}),
          ...(previewDetailsName ? { previewDetailsName } : {}),
          ...(name !== undefined ? { name } : {}),
          ...(ended !== undefined ? { ended } : {}),
          ...(custom ? { custom } : {}),
        };
      }),
    );
  };

  updateConversation: ConversationsState['updateConversation'] = (
    conversationId,
    conversationAttributes,
  ) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const current = draftState.conversations.data[conversationId];

        draftState.conversations.data[conversationId] = {
          ...current,
          ...conversationAttributes,
        };
      }),
    );
  };

  updateCategories = (categories, rootCategory) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.categories = categories;
        draftState.kb.rootCategory = rootCategory;
      }),
    );
  };

  updateArticles = (articles) => {
    const articlesToAdd = articles.reduce((acc, val) => {
      acc[val.articleId] = val;

      return acc;
    }, {});

    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articles = {
          ...this.state.kb.articles,
          ...articlesToAdd,
        };
      }),
    );
  };

  updateArticleSearchResults = (articles) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchResults = articles;
      }),
    );
  };

  updateArticleSearchInput = (term: string) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchInput = term;
      }),
    );
  };

  updateCurrentCategoryId = (categoryId) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.currentCategoryId = categoryId;
      }),
    );
  };

  updateCurrentArticleId = (articleId) => {
    kustomerCore.getKnowledgeBaseConfig().then((config) => {
      this.setState(
        produce((draftState: Draft<AppState>) => {
          draftState.kb.currentArticleId = articleId;
          draftState.kb.config = config;
        }),
      );
    });
  };

  updateArticleSearchFetching = (fetching: boolean) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchFetching = fetching;
      }),
    );
  };

  updateFeaturedArticles = (articles: ArticleCallbackResponse[]) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.featuredArticles = articles;
      }),
    );
    this.updateArticles(articles);
  };

  endConversation = (conversationId) => {
    kustomerCore.endConversation({
      conversationId,
    });
  };

  addOptimisticMessage = (conversationId, message) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.conversations.data[conversationId].optimisticMessages.push(
          message,
        );
      }),
    );
  };

  toggleOptimisticMessageError = (conversationId, optimisticId, value) => {
    this.setState((prevState) => {
      const { optimisticMessages } =
        prevState.conversations.data[conversationId];
      return produce(prevState, (draftState: Draft<AppState>) => {
        draftState.conversations.data[conversationId].optimisticMessages =
          optimisticMessages.map((message) => {
            if (message.messageId === optimisticId) {
              return {
                ...message,
                showError: value,
              };
            }
            return message;
          });
      });
    });
  };

  removeOptimisticMessage = (conversationId, optimisticMessageId) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const conversation = draftState.conversations.data[conversationId];

        let attachmentsToRevoke = [] as MessageAttachmentCallbackResponse[];

        const withoutOptimisticMessage = conversation.optimisticMessages.filter(
          (message) => {
            const isMessage = message.messageId === optimisticMessageId;

            if (isMessage && message.attachments) {
              attachmentsToRevoke = message.attachments;
            }

            return !isMessage;
          },
        );

        attachmentsToRevoke.forEach((attachment) => {
          if (attachment.url) URL.revokeObjectURL(attachment.url);
        });

        conversation.optimisticMessages = withoutOptimisticMessage;
      }),
    );
  };

  markArticleVisited = (messageId, articleId) => {
    this.setState((prevState) => {
      return produce(prevState, (draftState: Draft<AppState>) => {
        const message = draftState.messages.messageData[messageId];

        const article = message?.meta?.articles?.find(
          (article) => article.articleId === articleId,
        );

        if (article) {
          article.visited = 1;
        }
      });
    });
  };

  renderChatRootIcon() {
    const { navigation, unreadCounts, proactiveChat, isMobile } = this.state;
    const { hideChatIcon, chatSettings, chatAvailability, intlDefinitions } =
      this.props;

    if (hideChatIcon) {
      return null;
    }

    const proactiveChatContext = {
      proactiveChat,
      onCloseProactiveChat: () => this.handleProactiveChatTrigger(),
      handoffToMessageThread: this.handoffToMessageThread,
    };
    return (
      <IntlProvider definition={intlDefinitions}>
        <NavigationContext.Provider value={navigation}>
          <ChatAvailabilityContext.Provider value={chatAvailability}>
            <UnreadCountsContext.Provider value={unreadCounts}>
              <SettingsContext.Provider value={chatSettings}>
                <ProactiveChatContext.Provider value={proactiveChatContext}>
                  <ChatRootIcon
                    ref={this.chatRootIconRef}
                    onClick={this.openWidget}
                    isMobile={isMobile}
                  />
                </ProactiveChatContext.Provider>
              </SettingsContext.Provider>
            </UnreadCountsContext.Provider>
          </ChatAvailabilityContext.Provider>
        </NavigationContext.Provider>
      </IntlProvider>
    );
  }

  renderWidget() {
    const {
      navigation,
      widgetActions,
      conversations,
      messages,
      unreadCounts,
      kb,
      connection,
      isMobile,
      loading,
      retryCount,
    } = this.state;

    const {
      chatAvailability,
      chatSettings,
      hideNewConversationButtonSettings,
      intlDefinitions,
      hideHistory,
    } = this.props;

    return (
      <IntlProvider definition={intlDefinitions}>
        <NavigationContext.Provider value={navigation}>
          <ChatAvailabilityContext.Provider value={chatAvailability}>
            <HideNewConversationButtonSettingsContext.Provider
              value={hideNewConversationButtonSettings}
            >
              <SettingsContext.Provider value={chatSettings}>
                <WidgetActionsContext.Provider value={widgetActions}>
                  <ConversationsContext.Provider value={conversations}>
                    <UnreadCountsContext.Provider value={unreadCounts}>
                      <MessagesContext.Provider value={messages}>
                        <KBContext.Provider value={kb}>
                          <ConnectionContext.Provider value={connection}>
                            <Widget
                              isMobile={isMobile}
                              hideHistory={hideHistory}
                              loading={loading}
                              restartApp={
                                retryCount < 3 ? this.handleRestart : undefined
                              }
                            />
                          </ConnectionContext.Provider>
                        </KBContext.Provider>
                      </MessagesContext.Provider>
                    </UnreadCountsContext.Provider>
                  </ConversationsContext.Provider>
                </WidgetActionsContext.Provider>
              </SettingsContext.Provider>
            </HideNewConversationButtonSettingsContext.Provider>
          </ChatAvailabilityContext.Provider>
        </NavigationContext.Provider>
      </IntlProvider>
    );
  }

  render(_, state) {
    const { isChatHidden, showWidget } = state;
    if (isChatHidden) {
      return null;
    }
    return showWidget ? this.renderWidget() : this.renderChatRootIcon();
  }
}

export default App;
